import api from './client';
import type { WorkshopJob, WorkCharge, WorkshopWorker, CarWindow } from 'types/garage/atelier';

interface ApiCarWindow {
  deposit_date?: string | null;
  deposit_start_hour?: number | null;
  restitution_date?: string | null;
  restitution_start_hour?: number | null;
}

function toFrontendCarWindow(cw: ApiCarWindow): CarWindow {
  return {
    depositDate: cw.deposit_date ?? null,
    depositStartHour: cw.deposit_start_hour ?? null,
    restitutionDate: cw.restitution_date ?? null,
    restitutionStartHour: cw.restitution_start_hour ?? null,
  };
}

// Backend uses snake_case for some fields
interface ApiJob {
  id: string;
  org_id: string;
  client_name: string;
  plate: string;
  hours: number;
  worker_id: string;
  date: string;
  start_hour: number;
  duration_hours: number;
  status: string;
  specialty: string;
  has_car_icon: boolean;
  ordre_id?: string;
  intervention_id?: string;
  car_window?: ApiCarWindow | null;
}

function toFrontendJob(j: ApiJob): WorkshopJob {
  return {
    id: j.id,
    clientName: j.client_name,
    plate: j.plate,
    hours: j.hours,
    workerId: j.worker_id,
    date: j.date,
    startHour: j.start_hour,
    durationHours: j.duration_hours,
    status: j.status as WorkshopJob['status'],
    specialty: j.specialty as WorkshopJob['specialty'],
    hasCarIcon: j.has_car_icon,
    ordre_id: j.ordre_id,
    org_id: j.org_id,
    intervention_id: j.intervention_id,
    carWindow: j.car_window ? toFrontendCarWindow(j.car_window) : undefined,
  };
}

function toApiJob(j: Partial<WorkshopJob> & { org_id?: string }): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  if (j.clientName !== undefined) out.client_name = j.clientName;
  if (j.plate !== undefined) out.plate = j.plate;
  if (j.hours !== undefined) out.hours = j.hours;
  if (j.workerId !== undefined) out.worker_id = j.workerId;
  if (j.date !== undefined) out.date = j.date;
  if (j.startHour !== undefined) out.start_hour = j.startHour;
  if (j.durationHours !== undefined) out.duration_hours = j.durationHours;
  if (j.status !== undefined) out.status = j.status;
  if (j.specialty !== undefined) out.specialty = j.specialty;
  if (j.hasCarIcon !== undefined) out.has_car_icon = j.hasCarIcon;
  if (j.ordre_id !== undefined) out.ordre_id = j.ordre_id;
  if (j.intervention_id !== undefined) out.intervention_id = j.intervention_id;
  if (j.org_id !== undefined) out.org_id = j.org_id;
  return out;
}

interface ApiCharge {
  id: string;
  org_id: string;
  client_name: string;
  hours: number;
  status: string;
  specialty: string;
  ordre_id?: string;
  car_window?: ApiCarWindow | null;
}

function toFrontendCharge(c: ApiCharge): WorkCharge {
  return {
    id: c.id,
    clientName: c.client_name,
    hours: c.hours,
    status: c.status as WorkCharge['status'],
    specialty: c.specialty as WorkCharge['specialty'],
    ordre_id: c.ordre_id,
    org_id: c.org_id,
    carWindow: c.car_window ? toFrontendCarWindow(c.car_window) : undefined,
  };
}

function toApiCharge(c: Partial<WorkCharge> & { org_id?: string }): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  if (c.clientName !== undefined) out.client_name = c.clientName;
  if (c.hours !== undefined) out.hours = c.hours;
  if (c.status !== undefined) out.status = c.status;
  if (c.specialty !== undefined) out.specialty = c.specialty;
  if (c.ordre_id !== undefined) out.ordre_id = c.ordre_id;
  if (c.org_id !== undefined) out.org_id = c.org_id;
  return out;
}

interface ApiWorker {
  id: string;
  org_id: string;
  name: string;
  specialty: string;
  blocked_hours: number[];
}

function toFrontendWorker(w: ApiWorker): WorkshopWorker {
  return {
    id: w.id,
    name: w.name,
    specialty: w.specialty as WorkshopWorker['specialty'],
    blockedHours: w.blocked_hours,
  };
}

export const atelierApi = {
  // Jobs
  listJobs: async (params?: { org_id?: string; worker_id?: string; date?: string; limit?: number; offset?: number }): Promise<WorkshopJob[]> => {
    const { data } = await api.get('/atelier/jobs', { params });
    return (data as ApiJob[]).map(toFrontendJob);
  },

  createJob: async (job: Omit<WorkshopJob, 'id'> & { org_id: string }): Promise<WorkshopJob> => {
    const { data } = await api.post('/atelier/jobs', toApiJob(job));
    return toFrontendJob(data);
  },

  updateJob: async (id: string, updates: Partial<WorkshopJob>): Promise<WorkshopJob> => {
    const { data } = await api.patch(`/atelier/jobs/${id}`, toApiJob(updates));
    return toFrontendJob(data);
  },

  deleteJob: async (id: string): Promise<void> => {
    await api.delete(`/atelier/jobs/${id}`);
  },

  // Charges
  listCharges: async (params?: { org_id?: string; limit?: number }): Promise<WorkCharge[]> => {
    const { data } = await api.get('/atelier/charges', { params });
    return (data as ApiCharge[]).map(toFrontendCharge);
  },

  createCharge: async (charge: Omit<WorkCharge, 'id'> & { org_id: string }): Promise<WorkCharge> => {
    const { data } = await api.post('/atelier/charges', toApiCharge(charge));
    return toFrontendCharge(data);
  },

  updateCharge: async (id: string, updates: Partial<WorkCharge>): Promise<WorkCharge> => {
    const { data } = await api.patch(`/atelier/charges/${id}`, toApiCharge(updates));
    return toFrontendCharge(data);
  },

  deleteCharge: async (id: string): Promise<void> => {
    await api.delete(`/atelier/charges/${id}`);
  },

  // Workers
  listWorkers: async (params?: { org_id?: string }): Promise<WorkshopWorker[]> => {
    const { data } = await api.get('/atelier/workers', { params });
    return (data as ApiWorker[]).map(toFrontendWorker);
  },

  createWorker: async (worker: Omit<WorkshopWorker, 'id'> & { org_id: string }): Promise<WorkshopWorker> => {
    const { data } = await api.post('/atelier/workers', { org_id: worker.org_id, name: worker.name, specialty: worker.specialty, blocked_hours: worker.blockedHours || [] });
    return toFrontendWorker(data);
  },

  updateWorker: async (id: string, updates: Partial<WorkshopWorker>): Promise<WorkshopWorker> => {
    const payload: Record<string, unknown> = {};
    if (updates.name !== undefined) payload.name = updates.name;
    if (updates.specialty !== undefined) payload.specialty = updates.specialty;
    if (updates.blockedHours !== undefined) payload.blocked_hours = updates.blockedHours;
    const { data } = await api.patch(`/atelier/workers/${id}`, payload);
    return toFrontendWorker(data);
  },

  deleteWorker: async (id: string): Promise<void> => {
    await api.delete(`/atelier/workers/${id}`);
  },
};
